import { Link } from "react-router-dom";
import type { CashTransactionDto, DashboardSummaryDto } from "@grizzly/shared";
import { PageHeader, Card } from "../components/ui/card.js";
import { Badge } from "../components/ui/badge.js";
import { useSession, useHasPermission } from "../lib/session.js";
import { visibleNav, type NavItem } from "../lib/nav.js";
import { FollowUpQueue } from "../components/crm/followUpQueue.js";
import { useDashboardSummary } from "../lib/useDashboardSummary.js";
import { useI18n, type MessageKey } from "../i18n/index.js";
import { formatAmount, formatDateTime } from "../lib/format.js";

const SLICE_NOTES: Record<string, MessageKey> = {
  "/quotes": "dashboard.sliceNote.quotes",
  "/jobs": "dashboard.sliceNote.jobs",
  "/shop-floor": "dashboard.sliceNote.shopFloor",
  "/materials": "dashboard.sliceNote.materials",
  "/invoices": "dashboard.sliceNote.invoices",
  "/reports": "dashboard.sliceNote.reports",
};

function sliceNote(to: string): MessageKey {
  return SLICE_NOTES[to] ?? "dashboard.availableNow";
}

interface Kpi {
  label: string;
  value: string;
  to?: string;
}

function buildKpis(
  summary: DashboardSummaryDto,
  canFinancial: boolean,
  t: (key: MessageKey) => string
): Kpi[] {
  const kpis: Kpi[] = [
    {
      label: t("dashboard.kpi.overdueFollowUps"),
      value: String(summary.overdueFollowUps),
      to: "/customers",
    },
    {
      label: t("dashboard.kpi.openQuotes"),
      value: String(summary.openQuotes),
      to: "/quotes",
    },
    {
      label: t("dashboard.kpi.atRiskJobs"),
      value: String(summary.atRiskJobs),
      to: "/jobs",
    },
    {
      label: t("dashboard.kpi.openJobIssues"),
      value: String(summary.openJobIssues),
      to: "/jobs",
    },
    {
      label: t("dashboard.kpi.stockShortages"),
      value: String(summary.stockShortages),
      to: "/materials",
    },
  ];
  if (canFinancial) {
    kpis.push({
      label: t("dashboard.kpi.outstandingInvoices"),
      value: `${summary.outstandingInvoices} · ${formatAmount(summary.outstandingTotalMinor)}`,
      to: "/invoices",
    });
  }
  return kpis;
}

function KpiGrid({ kpis }: { kpis: Kpi[] }) {
  return (
    <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {kpis.map((kpi) => {
        const inner = (
          <>
            <p className="text-xs text-ink-muted">{kpi.label}</p>
            <p className="tabular text-lg font-semibold text-ink">{kpi.value}</p>
          </>
        );
        if (!kpi.to) {
          return (
            <div
              key={kpi.label}
              className="rounded-[var(--radius-card)] border border-line bg-surface-raised p-4"
            >
              {inner}
            </div>
          );
        }
        return (
          <Link
            key={kpi.label}
            to={kpi.to}
            className="rounded-[var(--radius-card)] border border-line bg-surface-raised p-4 transition-colors hover:border-line-strong"
          >
            {inner}
          </Link>
        );
      })}
    </div>
  );
}

function RecentCashCard({ transactions }: { transactions: CashTransactionDto[] }) {
  const { t } = useI18n();
  return (
    <Card className="mb-6">
      <h2 className="mb-2 text-sm font-semibold text-ink">{t("dashboard.recentCash")}</h2>
      <ul className="space-y-1">
        {transactions.map((tx) => (
          <li key={tx.id} className="flex items-center justify-between text-sm">
            <span className="text-ink-muted">
              {formatDateTime(tx.occurredAt)} ·{" "}
              {tx.categoryName ?? tx.description ?? tx.direction}
            </span>
            <span
              className={`tabular ${tx.direction === "in" ? "text-status-complete" : "text-status-risk"}`}
            >
              {tx.direction === "in" ? "+" : "-"}
              {formatAmount(tx.amountMinor)}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function DestinationGrid({ destinations }: { destinations: NavItem[] }) {
  const { t } = useI18n();
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {destinations.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          className="rounded-[var(--radius-card)] border border-line bg-surface-raised p-4 transition-colors hover:border-line-strong"
        >
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-[var(--radius-card)] bg-surface-inset text-action-primary-text">
              <item.icon className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-ink">
                {t(item.labelKey)}
              </p>
              <p className="text-xs text-ink-muted">{t(sliceNote(item.to))}</p>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

export function DashboardPage() {
  const { user } = useSession();
  const { t } = useI18n();
  const canOps = useHasPermission("reports:ops");
  const canFinancial = useHasPermission("reports:financial");
  const summary = useDashboardSummary(canOps);

  if (!user) return null;
  const destinations = visibleNav(user).filter((item) => item.to !== "/");
  const firstName = user.displayName.split(" ")[0] ?? user.displayName;
  const hasCrm = user.permissions.includes("customers:read");

  return (
    <>
      <PageHeader
        title={t("dashboard.welcome", { name: firstName })}
        description={hasCrm ? t("dashboard.descWithCrm") : t("dashboard.descBasic")}
      />

      {canOps && summary ? <KpiGrid kpis={buildKpis(summary, canFinancial, t)} /> : null}

      {canFinancial && summary && summary.recentCash.length > 0 ? (
        <RecentCashCard transactions={summary.recentCash} />
      ) : null}

      {hasCrm ? (
        <div className="mb-6 max-w-3xl">
          <FollowUpQueue userId={user.id} />
        </div>
      ) : null}

      <DestinationGrid destinations={destinations} />

      <div className="mt-6 flex items-center gap-2 text-xs text-ink-muted">
        <Badge tone="complete">S0–S8</Badge>
        <span>{t("dashboard.footer")}</span>
      </div>
    </>
  );
}
